import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  nom: "",
  societe: "",
  telephone: "",
  email: "",
  urgence: "",
  message: "",
  date_souhaitee: null,
};

export default function AuditForm() {
  const [form, setForm] = useState(() => ({
    ...initial,
    urgence: sessionStorage.getItem("prosecours_urgence") || "",
  }));
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const urgHandler = () => set("urgence", "Haute");
    window.addEventListener("prosecours:urgence", urgHandler);
    return () => {
      window.removeEventListener("prosecours:urgence", urgHandler);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.societe || !form.telephone || !form.email || !form.urgence || !form.message) {
      toast.error("Merci de remplir tous les champs requis.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        date_souhaitee: form.date_souhaitee
          ? form.date_souhaitee.toISOString()
          : null,
      };
      await axios.post(`${API}/audit-request`, payload);
      setSent(true);
      setForm(initial);
      sessionStorage.removeItem("prosecours_urgence");
      toast.success("Votre demande a bien été transmise. Nous revenons vers vous sous 48 h.");
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "ed-input w-full bg-transparent border-b border-[#1a1918]/30 rounded-none py-4 px-0 text-lg font-sans-ed text-[#1a1918] placeholder:text-[#1a1918]/40 focus:outline-none";

  return (
    <section
      id="audit"
      data-testid="audit-section"
      className="bg-[#f7f5f0] py-24 md:py-40"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="col-span-12 md:col-span-5"
        >
          <span data-testid="premier-echange-kicker" className="section-badge">§ Premier échange</span>
          <h2 className="mt-6 font-serif-ed text-5xl md:text-6xl lg:text-7xl leading-[1] tracking-tight text-[#1a1918]">
            Décrivez votre démarche.{" "}
            <span className="italic text-[#ce1e20]">Nous préparons</span>{" "}
            le dossier.
          </h2>
          <p className="mt-8 text-base md:text-lg leading-relaxed text-[#1a1918]/75 max-w-md">
            Vous indiquez la formalité ou la procédure retenue. Nous rédigeons
            les actes, réunissons les pièces, demandons la date auprès du
            tribunal si nécessaire et organisons le dépôt au greffe compétent.
          </p>

          <div className="mt-12 space-y-6 border-t border-[#1a1918]/25 pt-6 max-w-md">
            <div>
              <div className="overline text-[#1a1918]/60">Téléphone</div>
              <a
                href="tel:+33744883381"
                data-testid="contact-phone"
                className="ed-link mt-2 inline-block font-serif-ed text-3xl md:text-4xl text-[#1a1918]"
              >
                07 44 88 33 81
              </a>
            </div>
            <div>
              <div className="overline text-[#1a1918]/60">Email</div>
              <a
                href="mailto:contact@prosecours.fr"
                data-testid="contact-email"
                className="ed-link mt-2 inline-block font-serif-ed text-2xl md:text-3xl text-[#1a1918]"
              >
                contact@prosecours.fr
              </a>
            </div>
            <div>
              <div className="overline text-[#1a1918]/60">Zone d'intervention</div>
              <div className="mt-2 font-serif-ed text-xl text-[#1a1918]/85">
                Paris &amp; Île-de-France — France entière sur demande
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="audit-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="col-span-12 md:col-span-6 md:col-start-7 mt-12 md:mt-0"
        >
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            <div className="col-span-2 md:col-span-1">
              <label className="overline text-[#1a1918]/60">Nom</label>
              <input
                data-testid="input-nom"
                className={inputCls}
                value={form.nom}
                onChange={(e) => set("nom", e.target.value)}
                placeholder="Votre nom"
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <label className="overline text-[#1a1918]/60">Société</label>
              <input
                data-testid="input-societe"
                className={inputCls}
                value={form.societe}
                onChange={(e) => set("societe", e.target.value)}
                placeholder="Raison sociale"
              />
            </div>
            <div className="col-span-2 md:col-span-1 mt-4">
              <label className="overline text-[#1a1918]/60">Téléphone</label>
              <input
                data-testid="input-telephone"
                className={inputCls}
                value={form.telephone}
                onChange={(e) => set("telephone", e.target.value)}
                placeholder="+33 …"
              />
            </div>
            <div className="col-span-2 md:col-span-1 mt-4">
              <label className="overline text-[#1a1918]/60">Email</label>
              <input
                data-testid="input-email"
                type="email"
                className={inputCls}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="vous@societe.fr"
              />
            </div>

            <div className="col-span-2 md:col-span-1 mt-4">
              <label className="overline text-[#1a1918]/60">Urgence</label>
              <Select
                value={form.urgence}
                onValueChange={(v) => set("urgence", v)}
              >
                <SelectTrigger
                  data-testid="select-urgence"
                  className="ed-input w-full bg-transparent border-0 border-b border-[#1a1918]/30 rounded-none py-4 px-0 h-auto text-lg font-sans-ed text-[#1a1918] focus:ring-0 focus:border-[#1a1918] shadow-none"
                >
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent className="bg-[#f7f5f0] border-[#1a1918]/20 rounded-none">
                  <SelectItem value="Faible">Faible</SelectItem>
                  <SelectItem value="Moyenne">Moyenne</SelectItem>
                  <SelectItem value="Haute">Haute — critique</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 md:col-span-1 mt-4">
              <label className="overline text-[#1a1918]/60">
                Date souhaitée
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="input-date"
                    className="ed-input w-full bg-transparent border-b border-[#1a1918]/30 rounded-none py-4 px-0 text-lg font-sans-ed text-left flex items-center justify-between"
                  >
                    <span className={form.date_souhaitee ? "text-[#1a1918]" : "text-[#1a1918]/40"}>
                      {form.date_souhaitee
                        ? format(form.date_souhaitee, "dd MMMM yyyy", { locale: fr })
                        : "Choisir une date"}
                    </span>
                    <CalendarIcon size={18} className="text-[#1a1918]/60" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="bg-[#f7f5f0] border border-[#1a1918]/20 rounded-none p-2"
                >
                  <Calendar
                    mode="single"
                    selected={form.date_souhaitee}
                    onSelect={(d) => set("date_souhaitee", d)}
                    locale={fr}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="col-span-2 mt-4">
              <label className="overline text-[#1a1918]/60">
                Votre situation
              </label>
              <textarea
                data-testid="input-message"
                rows={4}
                className={`${inputCls} resize-none`}
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder="Trésorerie, litige, procédure en cours, cession…"
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <p className="text-xs text-[#1a1918]/50 max-w-sm">
              En envoyant ce formulaire, vous acceptez d'être recontacté par
              ProSecours — vos données sont traitées conformément au RGPD.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-audit"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#ce1e20] text-white overline hover:bg-[#a71518] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Envoi en cours…" : sent ? "Envoyé — merci" : "Envoyer ma demande"}
              <span>→</span>
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
